/**
 * Session Alerts Component
 *
 * Displays session-related alerts:
 * - Session expiry warning
 * - Duplicate login detection
 * - Force logout notification
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionSocket } from '../../hooks/useSessionSocket';
import { extendSession as extendSocketSession } from '../../services/socketService';

interface SessionExpiryWarning {
  minutesRemaining: number;
  expiresAt: number;
  message: string;
  canExtend: boolean;
  urgent?: boolean;
}

interface DuplicateLoginInfo {
  newDeviceInfo: {
    userAgent: string;
    ip: string;
    platform?: string;
    browser?: string;
  };
  timestamp: string;
  message: string;
  allowContinue: boolean;
}

interface ForceLogoutReason {
  reason: string;
  message: string;
  newDeviceInfo?: {
    platform?: string;
    browser?: string;
    ip: string;
  };
}

interface SessionAlertsProps {
  onLogout?: () => void;
  onExtendSession?: () => Promise<number | void>; // Returns new token expiry timestamp
}

export function SessionAlerts({ onLogout, onExtendSession }: SessionAlertsProps) {
  const navigate = useNavigate();
  const {
    expiryWarning,
    duplicateLoginInfo,
    forceLogoutReason,
    isSessionExpired,
    clearExpiryWarning,
    clearDuplicateLoginInfo,
    clearForceLogout
  } = useSessionSocket();

  const [countdown, setCountdown] = useState<number>(0);
  const [isExtending, setIsExtending] = useState(false);

  // Update countdown timer
  useEffect(() => {
    if (expiryWarning) {
      const updateCountdown = () => {
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((expiryWarning.expiresAt - now) / 1000));
        setCountdown(remaining);
      };

      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [expiryWarning]);

  // Handle session expiry (auto redirect)
  useEffect(() => {
    if (isSessionExpired) {
      handleLogout();
    }
  }, [isSessionExpired]);

  // Handle force logout
  useEffect(() => {
    if (forceLogoutReason && forceLogoutReason.reason === 'new-login') {
      // Immediate logout for new login from another device
      setTimeout(() => {
        handleLogout();
      }, 5000); // Give user 5 seconds to read the message
    }
  }, [forceLogoutReason]);

  const handleLogout = useCallback(() => {
    if (onLogout) {
      onLogout();
    } else {
      // Default logout behavior
      localStorage.removeItem('x-access-token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('auth_type');
      navigate('/login');
    }
  }, [onLogout, navigate]);

  const handleExtendSession = useCallback(async () => {
    setIsExtending(true);
    try {
      if (onExtendSession) {
        const newExpiry = await onExtendSession();
        if (newExpiry) {
          extendSocketSession(newExpiry);
        }
      }
      clearExpiryWarning();
    } catch (error) {
      console.error('Failed to extend session:', error);
    } finally {
      setIsExtending(false);
    }
  }, [onExtendSession, clearExpiryWarning]);

  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Session Expiry Warning Modal
  if (expiryWarning) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden ${expiryWarning.urgent ? 'ring-4 ring-red-500' : ''}`}>
          {/* Header */}
          <div className={`px-6 py-4 ${expiryWarning.urgent ? 'bg-red-500' : 'bg-orange-500'}`}>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-full">
                <i className={`${expiryWarning.urgent ? 'ri-alarm-warning-line' : 'ri-time-line'} text-white text-2xl`}></i>
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Session Expiring Soon</h3>
                <p className="text-white/80 text-sm">Your session will expire shortly</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Countdown */}
            <div className="text-center mb-6">
              <div className={`text-5xl font-bold ${countdown <= 60 ? 'text-red-500 animate-pulse' : 'text-orange-500'}`}>
                {formatCountdown(countdown)}
              </div>
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                Time remaining
              </p>
            </div>

            <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
              {expiryWarning.message}
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              {expiryWarning.canExtend && (
                <button
                  onClick={handleExtendSession}
                  disabled={isExtending}
                  className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isExtending ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Extending...
                    </>
                  ) : (
                    <>
                      <i className="ri-refresh-line mr-2"></i>
                      Extend Session
                    </>
                  )}
                </button>
              )}
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors flex items-center justify-center"
              >
                <i className="ri-logout-box-r-line mr-2"></i>
                Logout Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Duplicate Login Alert Modal
  if (duplicateLoginInfo) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-blue-500">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-full">
                <i className="ri-device-line text-white text-2xl"></i>
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">New Login Detected</h3>
                <p className="text-white/80 text-sm">Your account was accessed from another device</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <i className="ri-information-line text-blue-500 text-xl mt-0.5"></i>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  <p className="font-medium text-gray-900 dark:text-white mb-2">New Device Information:</p>
                  <div className="space-y-1">
                    <p><span className="text-gray-500">Platform:</span> {duplicateLoginInfo.newDeviceInfo.platform || 'Unknown'}</p>
                    <p><span className="text-gray-500">Browser:</span> {duplicateLoginInfo.newDeviceInfo.browser || 'Unknown'}</p>
                    <p><span className="text-gray-500">IP Address:</span> {duplicateLoginInfo.newDeviceInfo.ip}</p>
                    <p><span className="text-gray-500">Time:</span> {new Date(duplicateLoginInfo.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
              {duplicateLoginInfo.message}
            </p>

            {!duplicateLoginInfo.allowContinue && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 mb-4">
                <p className="text-red-600 dark:text-red-400 text-sm flex items-center">
                  <i className="ri-error-warning-line mr-2"></i>
                  You will be logged out shortly as concurrent sessions are not allowed.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              {duplicateLoginInfo.allowContinue && (
                <button
                  onClick={clearDuplicateLoginInfo}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center"
                >
                  <i className="ri-check-line mr-2"></i>
                  Continue Here
                </button>
              )}
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors flex items-center justify-center"
              >
                <i className="ri-logout-box-r-line mr-2"></i>
                Logout
              </button>
            </div>

            {duplicateLoginInfo.allowContinue && (
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
                <i className="ri-shield-check-line mr-1"></i>
                If this wasn't you, consider changing your password.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Force Logout Modal
  if (forceLogoutReason) {
    const getReasonTitle = (reason: string): string => {
      switch (reason) {
        case 'new-login':
          return 'Logged Out - New Login Detected';
        case 'logout-all-devices':
          return 'Logged Out - All Devices';
        case 'terminated-by-user':
          return 'Session Terminated';
        case 'admin-action':
          return 'Session Terminated by Administrator';
        default:
          return 'Session Ended';
      }
    };

    const getReasonIcon = (reason: string): string => {
      switch (reason) {
        case 'new-login':
          return 'ri-device-line';
        case 'logout-all-devices':
          return 'ri-logout-circle-line';
        case 'terminated-by-user':
          return 'ri-close-circle-line';
        case 'admin-action':
          return 'ri-admin-line';
        default:
          return 'ri-information-line';
      }
    };

    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-red-500">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-full">
                <i className={`${getReasonIcon(forceLogoutReason.reason)} text-white text-2xl`}></i>
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">{getReasonTitle(forceLogoutReason.reason)}</h3>
                <p className="text-white/80 text-sm">Your session has been terminated</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 text-center mb-4">
              {forceLogoutReason.message}
            </p>

            {forceLogoutReason.reason === 'new-login' && forceLogoutReason.newDeviceInfo && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">New Login From:</p>
                <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <p><span className="text-gray-500">Platform:</span> {forceLogoutReason.newDeviceInfo.platform || 'Unknown'}</p>
                  <p><span className="text-gray-500">Browser:</span> {forceLogoutReason.newDeviceInfo.browser || 'Unknown'}</p>
                  <p><span className="text-gray-500">IP:</span> {forceLogoutReason.newDeviceInfo.ip}</p>
                </div>
              </div>
            )}

            {forceLogoutReason.reason === 'new-login' && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 mb-4">
                <p className="text-yellow-700 dark:text-yellow-400 text-sm flex items-center">
                  <i className="ri-shield-keyhole-line mr-2"></i>
                  If this wasn't you, please secure your account immediately.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handleLogout}
                className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center"
              >
                <i className="ri-login-box-line mr-2"></i>
                Go to Login
              </button>

              {forceLogoutReason.reason === 'new-login' && (
                <button
                  onClick={() => {
                    clearForceLogout();
                    navigate('/change-password');
                  }}
                  className="w-full px-4 py-3 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 font-medium rounded-lg transition-colors flex items-center justify-center"
                >
                  <i className="ri-lock-password-line mr-2"></i>
                  Change Password
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Session Expired Modal
  if (isSessionExpired) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-700">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-full">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Session Expired</h3>
                <p className="text-white/80 text-sm">Please log in again to continue</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 mx-auto bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                <i className="ri-user-unfollow-line text-gray-400 text-4xl"></i>
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                Your session has expired due to inactivity. Please log in again to continue working.
              </p>
            </div>

            {/* Actions */}
            <button
              onClick={handleLogout}
              className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center"
            >
              <i className="ri-login-box-line mr-2"></i>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default SessionAlerts;
